import { CookieKey, ErrorMessages, MessageField } from '@/constants';
import firebaseApp from '@/lib/firebase/app';
import { deleteToken, getMessaging, getToken } from 'firebase/messaging';
import Cookies from 'js-cookie';
import getConfig from 'next/config';

const { publicRuntimeConfig } = getConfig();

const isWebPushSupported = () => 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;

/**
 * Deletes this browser's FCM registration so the push service stops delivering to it.
 * No-op when web push is unsupported or permission was never granted (no token can exist).
 */
export const deleteMessagingToken = async () => {
  if (typeof window === 'undefined' || !isWebPushSupported() || Notification.permission !== 'granted') {
    return;
  }

  await deleteToken(getMessaging(firebaseApp));
};

export class FirebaseMessaging {
  private static instance: FirebaseMessaging;

  static getInstance(): FirebaseMessaging {
    if (!FirebaseMessaging.instance) {
      FirebaseMessaging.instance = new FirebaseMessaging();
    }
    return FirebaseMessaging.instance;
  }

  private handleReceiveMessage = (event: MessageEvent) => {
    const data = event.data?.data;

    const browserUuid = data?.browser_uuid;
    const source = data?.source;

    if (browserUuid && source === 'parcel-monitor-community') {
      location.reload();
    }
  };

  private listenForegroundMessage = () => {
    navigator.serviceWorker.addEventListener('message', this.handleReceiveMessage);
  };

  private listenBackgroundMessage = () => {
    const channel = new BroadcastChannel('fcm-background');
    channel.addEventListener('message', this.handleReceiveMessage);
  };

  public initialize = () => {
    if (typeof window === 'undefined') {
      return;
    }

    if (!isWebPushSupported()) {
      console.error(ErrorMessages[MessageField.WEB_PUSH_NOTIFICATION_IS_NOT_SUPPORTED].error);
      return;
    }

    Notification.requestPermission()
      .then((permission) => {
        if (permission === 'granted') {
          const messaging = getMessaging(firebaseApp);
          getToken(messaging, { vapidKey: publicRuntimeConfig.firebaseVapidKey })
            .then((currentToken) => {
              if (currentToken) {
                Cookies.set(CookieKey.FirebaseMessageToken, currentToken, {
                  expires: 365,
                });
              } else {
                throw new Error(ErrorMessages[MessageField.FIREBASE_FCM_TOKEN_ERROR].error);
              }
            })
            .catch((err) => {
              console.error(ErrorMessages[MessageField.FIREBASE_FCM_TOKEN_ERROR].error, err);
            });
        }
      })
      .catch((err) => {
        console.error(ErrorMessages[MessageField.NOTIFICATION_PERMISSION_NOT_GRANTED].error, err);
      });

    this.listenForegroundMessage();
    this.listenBackgroundMessage();
  };
}

export const firebaseMessaging = FirebaseMessaging.getInstance();
